import { useState } from "react";
import { CommerceDialog } from "../../commerce/CommerceDialog";
import { Icone } from "./Icone";
import { normalizarChavePix, tipoChavePix } from "../../../utils/pix";

/* PIX manual (QR Code com a chave do vendedor).
   A chave liga/desliga salva sozinha. A chave em si é editada numa janela com
   "Salvar": ela vai para a loja pública, então um valor meio digitado nunca
   pode ser salvo sozinho. Com um provedor conectado (Mercado Pago, PagBank…),
   o PIX manual fica pausado nos pedidos — e, com o Mercado Pago, também na
   loja e no PDV —, porque o Pix do provedor confirma sozinho. */

const COR_PIX = "#25a26a";
export const mascararChave = chave => {
  const c = String(chave || "").trim();
  return c.length > 6 ? `••••${c.slice(-4)}` : c || "—";
};

export function LinhaPix({ cfg, pausadoPor = null, salvar, onEditar, onPorque }) {
  const pausado = !!pausadoPor;
  const naLoja = pausadoPor?.id === "mercadopago";
  const ativo = !!cfg.pixEnabled && !!String(cfg.pixChave || "").trim();
  // Com o Mercado Pago em uso, o Pix dele vale na loja, nos pedidos e no PDV; com
  // outro provedor, a chave sai só dos pedidos (segue na loja e no PDV)
  const status = !ativo ? { texto: "Desativado", classe: "" }
    : pausado ? { texto: naLoja ? "Pausado" : "PDV e loja", classe: "alerta" }
    : { texto: "Ativo", classe: "ok" };
  const alternar = () => {
    if (!cfg.pixEnabled && !String(cfg.pixChave || "").trim()) { onEditar(); return; }
    salvar({ pixEnabled: !cfg.pixEnabled });
  };
  return (
    <li className="pgw-item" style={{ "--gw-cor": COR_PIX }}>
      <div className="pgw-logo"><Icone nome="pix" /></div>
      <div className="pgw-texto">
        <div className="pgw-titulo">
          <span className="pgw-nome" id="pgw-pix-nome">PIX manual</span>
          <span className={`pgw-status ${status.classe}`}><i />{status.texto}</span>
        </div>
        <div className="pgw-sub">
          {pausado
            ? <>Fora {naLoja ? "da loja, dos pedidos e do PDV" : "dos pedidos"} enquanto {pausadoPor.artigo} {pausadoPor.nome} recebe o Pix{ativo || naLoja ? "." : "; ligue para usar a sua chave no PDV e na loja."} <button type="button" className="pgw-link" onClick={onPorque}>Por quê?</button></>
            : ativo ? <>QR Code com a sua chave <b>{mascararChave(cfg.pixChave)}</b></>
            : "Mostre um QR Code com a sua chave PIX."}
        </div>
      </div>
      <div className="pgw-acoes">
        <button type="button" role="switch" className="pgw-switch" aria-checked={!!cfg.pixEnabled} aria-labelledby="pgw-pix-nome" onClick={alternar} />
        <button type="button" className="pgw-btn" onClick={onEditar}>{cfg.pixChave ? "Editar" : "Configurar"}</button>
      </div>
    </li>
  );
}

export function JanelaPix({ cfg, salvar, onClose }) {
  const [form, setForm] = useState({ pixChave: cfg.pixChave || "", pixBeneficiario: cfg.pixBeneficiario || "", pixCidade: cfg.pixCidade || "" });
  const [erro, setErro] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const upd = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  async function enviar(e) {
    e?.preventDefault();
    const pixChave = normalizarChavePix(form.pixChave);
    if (!pixChave) { setErro("Informe a chave PIX."); return; }
    setOcupado(true);
    const ok = await salvar({ pixChave, pixBeneficiario: form.pixBeneficiario.trim(), pixCidade: form.pixCidade.trim(), pixEnabled: true });
    setOcupado(false);
    if (ok) onClose(); else setErro("Não foi possível salvar. Tente de novo.");
  }

  return (
    <CommerceDialog className="pgw-dialogo" title="PIX manual" subtitle="A chave aparece como QR Code na loja, nos pedidos e no PDV" onClose={() => !ocupado && onClose()}
      footer={<>
        <button type="button" className="pgw-btn" onClick={onClose} disabled={ocupado}>Cancelar</button>
        <button type="submit" form="pgw-form-pix" className="pgw-btn primario" style={{ "--b-cor": COR_PIX }} disabled={ocupado}>
          {ocupado ? <><span className="pgw-spin" aria-hidden="true" /> Salvando…</> : "Salvar e ativar"}
        </button>
      </>}>
      <form id="pgw-form-pix" className="pgw-etapa" onSubmit={enviar}>
        <div className="pgw-campos">
          <label className="pgw-campo inteiro">Chave PIX
            <input value={form.pixChave} onChange={e => upd("pixChave", e.target.value)} placeholder="CPF, CNPJ, e-mail, celular ou chave aleatória" autoComplete="off" autoFocus aria-label="Chave PIX" aria-describedby="pgw-pix-formato" />
            <small id="pgw-pix-formato" aria-live="polite">{form.pixChave.trim() ? <>{tipoChavePix(form.pixChave)}: <b>{normalizarChavePix(form.pixChave)}</b></> : "Celular com DDD, CPF, CNPJ, e-mail ou chave aleatória"}</small>
          </label>
          <label className="pgw-campo">Nome do beneficiário
            <input value={form.pixBeneficiario} onChange={e => upd("pixBeneficiario", e.target.value)} placeholder="NOME DA EMPRESA" maxLength={25} />
            <small>Até 25 caracteres</small>
          </label>
          <label className="pgw-campo">Cidade
            <input value={form.pixCidade} onChange={e => upd("pixCidade", e.target.value)} placeholder="SAO PAULO" />
          </label>
        </div>
        {erro && <div className="pgw-erro" role="alert">{erro}</div>}
      </form>
    </CommerceDialog>
  );
}

export function JanelaPorquePix({ gw, onGerenciar, onClose }) {
  const mp = gw.id === "mercadopago";
  return (
    <CommerceDialog className="pgw-dialogo" title="Por que o PIX manual está pausado?" onClose={onClose}
      footer={<>
        <button type="button" className="pgw-btn" onClick={onClose}>Entendi</button>
        <button type="button" className="pgw-btn primario" style={{ "--b-cor": gw.cor }} onClick={onGerenciar}>Gerenciar {gw.nome}</button>
      </>}>
      <div className="pgw-etapa">
        {mp ? <>
          <p>Com o <strong>Mercado Pago</strong> em uso, a loja, o “Receber pagamento” dos pedidos e o <strong>PDV</strong> cobram com o Pix dele — que confirma o pagamento sozinho e registra a venda. Mostrar também a sua chave PIX confundiria o cliente e não teria confirmação automática.</p>
          <p>A chave continua guardada. Para voltar a usá-la, desligue “Receber pelo Mercado Pago” em Gerenciar.</p>
        </> : <>
          <p>Com {gw.artigo} <strong>{gw.nome}</strong> conectad{gw.artigo}, o “Receber pagamento” dos pedidos cobra pela <strong>Cobrança online</strong> — que confirma o pagamento sozinha. O QR com a sua chave PIX não tem confirmação automática, por isso sai dos pedidos.</p>
          <p>A chave continua guardada e segue valendo na <strong>loja</strong> e no <strong>PDV</strong>. Para voltar a usá-la nos pedidos, desconecte {gw.artigo} {gw.nome} em Gerenciar.</p>
        </>}
      </div>
    </CommerceDialog>
  );
}
